import { useRef, useState } from 'react';
import { api, uploadTankPhoto } from '../api.js';
import { useConfig } from '../config.js';
import { formatVolume, defaultUnitsForNewTank } from '../units.js';
import { fillDateBounds, validFillDate, localDateString } from '../fill-date.js';

// Modal for creating a second+ tank after onboarding. Not the onboarding
// flow: no Sensei-first-read, no intent, no AI call. Same chip questions
// for water type / subtype / size / age so the experience is consistent.
// Respects the app's dark/light theme (onboarding forces light; this
// doesn't).
//
// All taxonomies (water types, subtypes, sizes, ages with midpoints + age
// days_ago + emojis + short labels) come from the server-side
// `onboarding_taxonomy` row via useConfig().

export function AddTankFlow({ onDone, onCancel, tanks }) {
  const taxonomy = useConfig()?.onboarding?.taxonomy;
  const ages = taxonomy?.ages || [];
  const lookup = (list, k) => list.find((x) => x.k === k);
  const subtypeMeta = (slug) =>
    slug
      ? [...(taxonomy?.subtypes?.saltwater || []), ...(taxonomy?.subtypes?.freshwater || [])].find(
          (s) => s.k === slug
        )
      : null;
  const [step, setStep] = useState('purpose');
  const startUnits = defaultUnitsForNewTank(tanks);
  const [data, setData] = useState({
    purpose: null,
    water_type: null,
    subtype: null,
    size: null,
    size_gallons: '',
    // Per-tank display unit preference, picked on the size step.
    // Storage stays canonical gallons + °F server-side — these only
    // steer what the keeper sees + types on this tank.
    display_volume_unit: startUnits.volume,
    display_temp_unit: startUnits.temp,
    age: null,
    name: '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const patch = (partial, next) => {
    setData((d) => ({ ...d, ...partial }));
    if (next) setStep(next);
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      setErr("That doesn't look like an image.");
      return;
    }
    setErr('');
    setPhotoFile(f);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const create = async () => {
    setBusy(true);
    setErr('');
    try {
      const qt = data.purpose === 'quarantine';
      // A QT is a fish tank for a short stay: the fish-only profile gives it
      // fish ranges for temperature and nitrate and hides reef chemistry.
      // A skipped style keeps the water type: an unknown slug reads as
      // saltwater (server/water-type.js), which gave a freshwater tank reef
      // cards and chores.
      const subtype = qt
        ? data.water_type === 'freshwater'
          ? 'community'
          : 'fowlr'
        : data.subtype ||
          (data.water_type === 'freshwater'
            ? 'other-freshwater'
            : data.water_type === 'saltwater'
              ? 'undecided-saltwater'
              : null);
      const subMeta = subtypeMeta(subtype);
      const subtypeLabel = qt
        ? 'Quarantine'
        : subMeta?.short_label || (data.water_type === 'freshwater' ? 'Freshwater' : 'Saltwater');
      // Typed volume only — the bucket UI was removed (it was redundant
      // when the user could just type the exact number). Validates
      // 1..10_000; anything outside that is dropped.
      const typed = Number(data.size_gallons);
      const volumeGallons = Number.isFinite(typed) && typed >= 1 && typed <= 10_000 ? typed : null;
      const ageDays = lookup(ages, data.age)?.days_ago ?? null;
      const filled = data.age === '<1mo' ? validFillDate(data.filled_on) : null;
      const estDate =
        filled ||
        (ageDays != null ? localDateString(new Date(Date.now() - ageDays * 86400000)) : null);
      const subtitleBits = [];
      // In the keeper's own unit and rounded: a litres keeper's 200 L used
      // to be stored as "52.83441047162968 gal".
      if (volumeGallons)
        subtitleBits.push(
          formatVolume(volumeGallons, data.display_volume_unit === 'L' ? 'L' : 'gal')
        );
      // A QT's badge already says what it is; "20 gal · quarantine" beside
      // it read twice.
      if (subtypeLabel && !qt) subtitleBits.push(subtypeLabel.toLowerCase());
      const created = await api('/api/tanks', {
        method: 'POST',
        body: {
          name: data.name.trim() || 'My tank',
          subtitle: subtitleBits.join(' · ') || null,
          volume_gallons: volumeGallons,
          type: subtypeLabel,
          emoji: subMeta?.emoji || '🌊',
          established_date: estDate,
          subtype_slug: subtype || null,
          purpose: qt ? 'quarantine' : 'display',
          // "Still planning": no water yet, so no starter chores.
          planning: data.age === 'planning',
          // Per-tank display preference — server's sanitizeDisplayUnits
          // drops anything outside the allow-list.
          display_units: {
            volume: data.display_volume_unit,
            temp: data.display_temp_unit,
          },
        },
      });
      if (photoFile) {
        try {
          await uploadTankPhoto(created.id, photoFile);
        } catch (e) {
          console.error('Photo upload failed:', e); /* non-blocking */
        }
      }
      onDone(created);
    } catch (e) {
      setErr(e.message || 'Could not create the tank.');
      setBusy(false);
    }
  };

  return (
    <div
      className="at-overlay"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="at-title"
    >
      <div className="at-sheet" onClick={(e) => e.stopPropagation()}>
        <header className="at-header">
          <h2 id="at-title" className="font-heading">
            ADD A TANK
          </h2>
          <button type="button" aria-label="Cancel" className="at-close" onClick={onCancel}>
            ×
          </button>
        </header>

        <div className="at-body">
          {step === 'purpose' && (
            <StepPurpose value={data.purpose} onPick={(v) => patch({ purpose: v }, 'water')} />
          )}
          {step === 'water' && (
            <StepWater
              options={(taxonomy?.water_types || []).filter((o) => o.k !== 'unsure')}
              value={data.water_type}
              onPick={(v) =>
                patch(
                  { water_type: v, subtype: null },
                  data.purpose === 'quarantine' ? 'size' : 'subtype'
                )
              }
              onBack={() => setStep('purpose')}
            />
          )}
          {step === 'subtype' && (
            <StepSubtype
              water={data.water_type}
              options={(
                taxonomy?.subtypes?.[
                  data.water_type === 'freshwater' ? 'freshwater' : 'saltwater'
                ] || []
              ).filter((o) => !o.only_intent)}
              value={data.subtype}
              onPick={(v) => patch({ subtype: v }, 'size')}
              onBack={() => setStep('water')}
            />
          )}
          {step === 'size' && (
            <StepSize
              gallons={data.size_gallons}
              volumeUnit={data.display_volume_unit}
              tempUnit={data.display_temp_unit}
              onGallons={(g) => patch({ size_gallons: g })}
              onVolumeUnit={(u) => patch({ display_volume_unit: u })}
              onTempUnit={(u) => patch({ display_temp_unit: u })}
              onContinue={() => setStep('age')}
              onBack={() => setStep(data.purpose === 'quarantine' ? 'water' : 'subtype')}
            />
          )}
          {step === 'age' && (
            <StepAge
              options={ages}
              value={data.age}
              filledOn={data.filled_on}
              onPick={(v) => patch({ age: v, filled_on: null }, 'name')}
              onFilled={(d) => patch({ age: '<1mo', filled_on: d }, 'name')}
              onBack={() => setStep('size')}
            />
          )}
          {step === 'name' && (
            <StepName
              name={data.name}
              onName={(v) => patch({ name: v })}
              photoPreview={photoPreview}
              onPickPhoto={() => fileRef.current?.click()}
              onRemovePhoto={() => {
                if (photoPreview) URL.revokeObjectURL(photoPreview);
                setPhotoFile(null);
                setPhotoPreview(null);
              }}
              onBack={() => setStep('age')}
              onSave={create}
              busy={busy}
              err={err}
            />
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onPhoto}
            style={{ display: 'none' }}
          />
        </div>
      </div>
    </div>
  );
}

function ChipList({ options, value, onPick, columns = 1 }) {
  return (
    <div className="at-chips" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {options.map((o) => (
        <button
          key={o.k}
          type="button"
          className={`at-chip ${value === o.k ? 'active' : ''}`}
          onClick={() => onPick(o.k)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function StepPurpose({ value, onPick }) {
  return (
    <>
      <p className="at-q">What is this tank for?</p>
      <ChipList
        options={[
          { k: 'display', label: 'A display tank' },
          { k: 'quarantine', label: 'Quarantine or hospital tank' },
        ]}
        value={value}
        onPick={onPick}
      />
      <p className="at-exact-hint">
        A quarantine tank gets ammonia and nitrite cards and no reef chores. Only temperature,
        ammonia, nitrite and pH are graded there, so low salinity during hyposalinity is not an
        alarm.
      </p>
    </>
  );
}

function StepWater({ options, value, onPick, onBack }) {
  return (
    <>
      <p className="at-q">Is it saltwater or freshwater?</p>
      <ChipList options={options} value={value} onPick={onPick} />
      <BackLink onClick={onBack} />
    </>
  );
}

function StepSubtype({ water, options, value, onPick, onBack }) {
  return (
    <>
      <p className="at-q">
        {water === 'freshwater' ? "What's the style?" : 'What kind of saltwater tank?'}
      </p>
      <ChipList
        options={options.map((o) => ({ k: o.k, label: o.short_label || o.label }))}
        value={value}
        onPick={onPick}
      />
      <BackLink onClick={onBack} />
    </>
  );
}

function StepSize({
  gallons,
  volumeUnit,
  tempUnit,
  onGallons,
  onVolumeUnit,
  onTempUnit,
  onContinue,
  onBack,
}) {
  const typed = Number(gallons);
  const valid = Number.isFinite(typed) && typed >= 1 && typed <= 10_000;
  const canContinue = valid;
  const isLitres = volumeUnit === 'L';
  // Show the typed canonical gallons in whatever unit the keeper picked.
  // Empty stays empty so they can clear the field.
  const inputValue =
    isLitres && Number.isFinite(typed) && typed > 0
      ? (typed * 3.785411784).toFixed(1).replace(/\.0$/, '')
      : gallons || '';
  const onInput = (raw) => {
    const n = Number(String(raw).trim().replace(',', '.'));
    if (raw === '' || !Number.isFinite(n)) {
      onGallons('');
      return;
    }
    onGallons(String(isLitres ? n / 3.785411784 : n));
  };
  return (
    <>
      <p className="at-q">How big is the tank?</p>
      <div className="at-exact">
        <div className="at-exact-row">
          <input
            id="at-size-exact"
            type="number"
            inputMode="decimal"
            min="1"
            max="10000"
            step="0.1"
            placeholder={isLitres ? 'e.g. 110' : 'e.g. 29'}
            value={inputValue}
            onChange={(e) => onInput(e.target.value)}
            className="at-input"
            autoFocus
          />
          <AtUnitToggle
            ariaLabel="Volume unit"
            value={volumeUnit}
            options={[
              { k: 'gal', label: 'gal' },
              { k: 'L', label: 'L' },
            ]}
            onPick={onVolumeUnit}
          />
        </div>
        <div className="at-exact-row at-exact-temp-row">
          <span className="at-exact-label font-heading" style={{ flex: 1 }}>
            Temperature display
          </span>
          <AtUnitToggle
            ariaLabel="Temperature unit"
            value={tempUnit}
            options={[
              { k: 'F', label: '°F' },
              { k: 'C', label: '°C' },
            ]}
            onPick={onTempUnit}
          />
        </div>
        <p className="at-exact-hint">
          Type the working volume of your tank. Units stay with this tank; you can change them later
          in Tank settings.
        </p>
      </div>
      <div className="at-actions">
        <button type="button" className="at-secondary" onClick={onBack}>
          Back
        </button>
        <button type="button" className="at-primary" onClick={onContinue} disabled={!canContinue}>
          Continue
        </button>
      </div>
    </>
  );
}

// Inline segmented control. Same visual as the onboarding ObUnitToggle
// so the keeper sees a consistent pattern across all tank-creation
// flows.
function AtUnitToggle({ value, options, onPick, ariaLabel }) {
  return (
    <div className="at-unit-toggle" role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.k}
          type="button"
          className={`at-unit-chip${value === o.k ? ' is-active' : ''}`}
          onClick={() => onPick(o.k)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function StepAge({ options, value, filledOn, onPick, onFilled, onBack }) {
  const [askDay, setAskDay] = useState(value === '<1mo');
  const { min, max } = fillDateBounds();
  const [day, setDay] = useState(validFillDate(filledOn) || max);
  return (
    <>
      <p className="at-q">When did you set it up?</p>
      <ChipList
        options={options.map((o) => ({ k: o.k, label: o.short_label || o.label }))}
        value={value}
        onPick={(v) => {
          // Under a month: ask for the day rather than guessing two weeks.
          if (v === '<1mo') setAskDay(true);
          else onPick(v);
        }}
      />
      {askDay && (
        <div style={{ marginTop: 12 }}>
          <label className="at-q" htmlFor="at-filled-on" style={{ display: 'block' }}>
            When was it filled?
          </label>
          <input
            id="at-filled-on"
            type="date"
            min={min}
            max={max}
            value={day}
            onChange={(e) => setDay(e.target.value)}
            style={{ minHeight: 44, width: '100%', boxSizing: 'border-box' }}
          />
          <button
            type="button"
            className="at-primary"
            style={{ marginTop: 12, minHeight: 44 }}
            disabled={!validFillDate(day)}
            onClick={() => onFilled(day)}
          >
            Continue
          </button>
        </div>
      )}
      <BackLink onClick={onBack} />
    </>
  );
}

function StepName({
  name,
  onName,
  photoPreview,
  onPickPhoto,
  onRemovePhoto,
  onBack,
  onSave,
  busy,
  err,
}) {
  return (
    <>
      <p className="at-q">Give it a name.</p>
      <input
        type="text"
        value={name}
        onChange={(e) => onName(e.target.value)}
        placeholder="The 120"
        maxLength={60}
        className="at-input"
        autoFocus
      />

      <p className="at-q" style={{ marginTop: 18 }}>
        A photo, if you have one.
      </p>
      {photoPreview ? (
        <div className="at-thumb">
          <img src={photoPreview} alt="" />
          <button type="button" className="at-thumb-remove" onClick={onRemovePhoto}>
            Remove
          </button>
        </div>
      ) : (
        <button type="button" className="at-photo-btn" onClick={onPickPhoto}>
          + Add photo
        </button>
      )}

      {err && (
        <p role="alert" className="at-err">
          {err}
        </p>
      )}
      <div className="at-actions">
        <button type="button" className="at-secondary" onClick={onBack} disabled={busy}>
          Back
        </button>
        <button type="button" className="at-primary" onClick={onSave} disabled={busy}>
          {busy ? 'Creating…' : 'Create tank'}
        </button>
      </div>
    </>
  );
}

function BackLink({ onClick }) {
  return (
    <button type="button" className="at-back" onClick={onClick}>
      ← Back
    </button>
  );
}
